import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { Plus, Trash2, Edit2, Package as PackageIcon } from 'lucide-react';
import { toast } from 'sonner';

const emptyProductForm = {
  code: '',
  name: '',
  description: '',
  price: '',
};

const ProductsPage = () => {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [formData, setFormData] = useState(emptyProductForm);
  const [productStep, setProductStep] = useState(1);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [initialStockQuantities, setInitialStockQuantities] = useState({});

  // Pagination state
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const pagination = usePagination({
    initialPage: 1,
    itemsPerPage: 10,
    totalItems: paginationData.totalItems,
  });

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, debouncedSearchTerm]);

  useEffect(() => {
    const loadStocks = async () => {
      if (!isAdmin()) return;
      try {
        const response = await api.getStocks({ page: 1, limit: 100 });
        setStocks(response.data || []);
      } catch (error) {
        toast.error('Erro ao carregar stocks');
        setStocks([]);
      }
    };

    loadStocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await api.getProducts({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: debouncedSearchTerm,
      });
      
      // Handle both paginated and non-paginated responses for backward compatibility
      if (response.data && response.pagination) {
        setProducts(response.data);
        setPaginationData(response.pagination);
      } else {
        // Fallback for non-paginated response
        setProducts(Array.isArray(response) ? response : []);
        setPaginationData({
          currentPage: 1,
          totalPages: 1,
          totalItems: Array.isArray(response) ? response.length : 0,
          itemsPerPage: 10,
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar produtos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductStep(1);
      setFormData({
        code: product.code,
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
      });
    } else {
      setEditingProduct(null);
      setProductStep(1);
      setFormData(emptyProductForm);
      setSelectedStockIds([]);
      setInitialStockQuantities({});
    }
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingProduct(null);
      setProductStep(1);
      setFormData(emptyProductForm);
      setSelectedStockIds([]);
      setInitialStockQuantities({});
    }
  };

  const handleProductDetailsNext = (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name || !formData.price) return;
    setProductStep(2);
  };

  const toggleInitialStock = (stockId, checked) => {
    setSelectedStockIds((current) =>
      checked
        ? Array.from(new Set([...current, stockId]))
        : current.filter((id) => id !== stockId)
    );
    if (!checked) {
      setInitialStockQuantities((current) => {
        const next = { ...current };
        delete next[stockId];
        return next;
      });
    }
  };

  const selectedInitialStocks = () => selectedStockIds.map((stockId) => ({
    stockId,
    quantity: Number(initialStockQuantities[stockId]),
  }));

  const validateInitialStocks = () => {
    const initialStocks = selectedInitialStocks();
    if (initialStocks.length === 0) {
      toast.error('Selecione pelo menos um stock inicial');
      return null;
    }
    if (initialStocks.some((item) => !Number.isInteger(item.quantity) || item.quantity <= 0)) {
      toast.error('Informe uma quantidade positiva para cada stock selecionado');
      return null;
    }
    return initialStocks;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, data);
        toast.success('Produto atualizado com sucesso');
      } else {
        const initialStocks = validateInitialStocks();
        if (!initialStocks) return;
        await api.createProduct({ ...data, initialStocks });
        toast.success('Produto criado com sucesso');
      }

      setDialogOpen(false);
      setEditingProduct(null);
      setFormData(emptyProductForm);
      setProductStep(1);
      setSelectedStockIds([]);
      setInitialStockQuantities({});
      loadProducts();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar produto');
    }
  };

  const handleToggleEnabled = async (product) => {
    try {
      await api.toggleProduct(product.id);
      toast.success(`Produto ${!product.enabled ? 'ativado' : 'desativado'} com sucesso`);
      loadProducts();
    } catch (error) {
      toast.error('Erro ao alterar status do produto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;
    try {
      await api.deleteProduct(id);
      toast.success('Produto deletado');
      loadProducts();
    } catch (error) {
      toast.error('Erro ao deletar produto');
    }
  };

  const handlePageChange = (page) => {
    pagination.goToPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const renderProductFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="code">Código</Label>
        <Input
          id="code"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Nome do Produto</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Descrição</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="price">Preço (R$)</Label>
        <Input
          id="price"
          type="number"
          step="0.01"
          min="0"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          required
        />
      </div>
    </>
  );

  const renderInitialStockStep = () => (
    <div className="space-y-4">
      <div className="rounded-md border">
        {stocks.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Nenhum stock cadastrado.</div>
        ) : stocks.map((stock) => {
          const checked = selectedStockIds.includes(stock.id);
          return (
            <label key={stock.id} className="grid cursor-pointer grid-cols-[auto_1fr_8rem] items-center gap-3 border-b p-3 text-sm last:border-b-0">
              <Checkbox
                checked={checked}
                onCheckedChange={(value) => toggleInitialStock(stock.id, Boolean(value))}
              />
              <span>
                <span className="font-mono">{stock.code}</span>
                <span className="ml-2">{stock.name}</span>
              </span>
              <Input
                type="number"
                min="1"
                step="1"
                placeholder="Qtd."
                value={initialStockQuantities[stock.id] || ''}
                disabled={!checked}
                onChange={(e) => setInitialStockQuantities({ ...initialStockQuantities, [stock.id]: e.target.value })}
              />
            </label>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => setProductStep(1)}>
          Voltar
        </Button>
        <Button type="submit" className="flex-1">
          Finalizar cadastro
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Produtos</h1>
          <p className="text-muted-foreground mt-1">Gerencie o catálogo de produtos</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-product-button">
                <Plus className="w-4 h-4 mr-2" />
                Novo Produto
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[620px]">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Editar Produto' : 'Criar Novo Produto'}</DialogTitle>
                <DialogDescription>
                  {editingProduct ? 'Atualize os dados do produto' : `Step ${productStep} de 2`}
                </DialogDescription>
              </DialogHeader>
              {editingProduct ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {renderProductFields()}
                  <Button type="submit" className="w-full">Atualizar Produto</Button>
                </form>
              ) : productStep === 1 ? (
                <form onSubmit={handleProductDetailsNext} className="space-y-4">
                  {renderProductFields()}
                  <Button type="submit" className="w-full">Próximo</Button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {renderInitialStockStep()}
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search Input */}
      <SearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Pesquisar por código, nome ou descrição..."
        searchFields={['Código', 'Nome', 'Descrição']}
      />

      {/* Products Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Descrição</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Preço</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="border-b hover:bg-muted/50 transition-colors"
                  data-testid={`product-row-${product.id}`}
                >
                  <td className="px-6 py-4 text-sm font-mono">{product.code}</td>
                  <td className="px-6 py-4 text-sm font-medium">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground line-clamp-1">
                    {product.description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-right">
                    R$ {product.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? (
                      <Switch
                        checked={product.enabled}
                        onCheckedChange={() => handleToggleEnabled(product)}
                        aria-label={`${product.enabled ? 'Desativar' : 'Ativar'} produto`}
                      />
                    ) : (
                      <Badge variant={product.enabled ? 'default' : 'secondary'}>
                        {product.enabled ? 'Ativo' : 'Inativo'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {isAdmin() && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {paginationData.totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(paginationData.currentPage - 1)}
                className={paginationData.currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => handlePageChange(page)}
                  isActive={page === paginationData.currentPage}
                  className="cursor-pointer"
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(paginationData.currentPage + 1)}
                className={paginationData.currentPage === paginationData.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {products.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <PackageIcon className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchTerm ? 'Nenhum produto encontrado.' : 'Nenhum produto encontrado.'}
              {isAdmin() && !searchTerm && ' Crie o primeiro!'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductsPage;
